import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ToastAction } from '@/components/ui/toast';
import { toast } from '@/hooks/use-toast';
import i18n from 'i18next';
import { navigate } from 'wouter/use-browser-location';
/**
 * Surface the "a cockpit window was blocked" guidance. Chrome allows only one
 * pop-up per click unless the site is allow-listed, so a second cockpit window
 * gets silently blocked — this turns that silent failure into a clear,
 * actionable prompt with a one-click retry (meaningful once the user has
 * allowed pop-ups) and a shortcut into Settings → Display, where the pop-up
 * preflight card walks through allow-listing step by step.
 * Non-destructive tone: this is a setup step, not an error.
 *
 * Called from event handlers (not React render), so it uses the i18n instance
 * directly rather than the useTranslation hook, and wouter's non-hook
 * navigate() for the settings deep-link (keeps SPA state — no reload).
 */
export function notifyPopupBlocked(blockedLeg, onRetry) {
    const label = i18n.t(`multiScreen.leg.${blockedLeg}`, { ns: 'common' });
    // The toast handle only exists after toast() returns, but the description
    // link (created first) needs it to dismiss the toast when the user jumps to
    // Settings — hold it in a const ref box.
    const handleRef = {};
    const openSettings = () => {
        navigate('/settings/display');
        handleRef.current?.dismiss();
    };
    handleRef.current = toast({
        title: i18n.t('multiScreen.popupBlocked.title', { ns: 'common', leg: label }),
        description: (_jsxs("span", { children: [i18n.t('multiScreen.popupBlocked.body', { ns: 'common', leg: label }), ' ', _jsx("button", { type: "button", onClick: openSettings, className: "font-medium underline underline-offset-2 hover:opacity-80", children: i18n.t('multiScreen.popupBlocked.fixLink', { ns: 'common' }) })] })),
        // Actionable setup guidance — give the user time to read and click
        // through instead of the default quick-dismiss.
        duration: 15000,
        action: (_jsx(ToastAction, { altText: i18n.t('multiScreen.popupBlocked.retryAlt', { ns: 'common', leg: label }), onClick: onRetry, children: i18n.t('multiScreen.popupBlocked.retry', { ns: 'common' }) })),
    });
}
